import React from "react"
import { usePageContext } from "vike-react/usePageContext"
import { twMerge } from "tailwind-merge"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import { CheckBadgeIcon, EnvelopeIcon, PhoneIcon, TruckIcon, ArrowUturnLeftIcon, MapPinIcon, DocumentTextIcon } from "@heroicons/react/24/outline"
import Typography from "aldoo-ra/Typography"
import { orgLogoUrl } from "aldoo-ra/Organizations/api"
import Chip from "../ui/Chip"
import CategoryItemBrowser from "../ui/CategoryItemBrowser"
import useSearchParams, { setSearchParams } from "../hooks/useSearchParams"
import Spinner from "../ui/Spinner"
import useT from "../hooks/useT"
import useAsync from "../hooks/useAsync"
import useSiteLanguage, { useCurrency } from "../hooks/useSiteLanguage"
import { usePageTitle } from "../hooks/usePageTitle"
import { LoadSeller, ListSellerProducts } from "../api"
import { langFromPath, stripLang } from "../utils/contentRoutes"
import { formatMoney } from "../account/ui"
import { render } from "vike/abort"
import { languageUrls, sellerJsonLd, seoTexts } from "../utils/seo"

const PAGE_PX = "px-4 sm:px-[30px] md:px-[80px] lg:px-[160px] xl:px-[280px]"

/**
 * `/seller/:slug` opens on what the seller sells; `/seller/:slug/about` is who
 * they are: how to reach them, how they deliver, how goods go back. The slug
 * is the company id every product id carries (Organization.cid).
 */
function sellerSlugFromPath(pathname) {
  const segments = stripLang(pathname || "/").split("/").filter(Boolean)
  return { slug: segments[1], isProducts: segments[2] !== "about" }
}

// Server-side seller load: <title>, description and og:image
async function onBeforeRender(pageContext) {
  if (pageContext.is404) return {}
  const pathname = pageContext.urlParsed?.pathname || pageContext.urlPathname || "/"
  const { slug } = sellerSlugFromPath(pathname)
  if (!slug) return {}
  try {
    const seller = await Promise.race([
      LoadSeller({ slug, language: langFromPath(pathname) || "en" }),
      new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 4000)),
    ])
    // the API says there is no such seller: a 404
    if (seller?.error) throw render(404)
    if (!seller?.name) return {}
    const language = langFromPath(pathname) || "en"
    const { canonicalUrl } = languageUrls(pathname, pageContext.supportedLanguages)
    return {
      title: `${seller.name} | Aldoo`,
      description: seoTexts(language).seller(seller.name),
      thumbnail: seller.logo ? orgLogoUrl(seller.id, seller.logoUpdatedAt) : undefined,
      metadata: { ogType: "profile", jsonLd: sellerJsonLd(seller, { language, url: canonicalUrl }) },
      storefrontSeller: seller,
    }
  } catch (error) {
    if (error && typeof error === "object" && "_isAbortError" in error) throw error
    console.error("Storefront seller onBeforeRender:", error?.message || error)
    return {}
  }
}

function TabChip({ label, to, active }) {
  return <Chip label={label} to={to} tone={active ? "dark" : "outline"} className="min-w-[90px] justify-center px-5" />
}

/**
 * What the seller sells, as a category lists it (CategoryItemBrowser: sort,
 * grid or list, pages in the URL), with the seller's own content types to
 * narrow it — one request for their counts, the browser for the products.
 */
function ProductsTab({ slug }) {
  const t = useT()
  const language = useSiteLanguage()
  const currency = useCurrency()
  const params = useSearchParams()
  const contentType = params.get("type") || null
  // the counts of each type: the first page of the seller's listing carries them
  const { result } = useAsync(() => ListSellerProducts({ slug, page: 0, items_per_page: 1, language, currency }), [slug, language, currency])
  const types = result?.contentTypes || []
  const total = types.reduce((sum, ct) => sum + (ct.count || 0), 0)
  const selectType = (type) => setSearchParams({ type: type || null, page: 1 })
  return (
    <div className="flex flex-col gap-6" data-testid="seller-products">
      {/* the type filter only when there is something to choose between */}
      {types.length > 1 && (
        <div className="flex flex-row flex-wrap items-center gap-2 border-b border-bordered pb-4">
          {[{ type: null, name: t`All`, count: total }, ...types].map((ct) => (
            <Chip
              key={ct.type ?? "all"}
              tone={contentType === ct.type ? "primary" : "outline"}
              onClick={() => selectType(ct.type)}
              className="min-w-[110px] justify-between gap-2"
              label={
                <>
                  <span>{ct.name || ct.type}</span>
                  <span className="text-[12px] font-bold">{ct.count}</span>
                </>
              }
            />
          ))}
        </div>
      )}
      <CategoryItemBrowser key={`${slug}:${contentType || "all"}`} placement="seller_page" publisher={slug} contentTypes={contentType ? [contentType] : undefined} />
    </div>
  )
}

function InfoCard({ icon: Icon, title, children, testid }) {
  return (
    <section className="rounded-2xl border border-bordered bg-paper p-5 flex flex-col gap-2" data-testid={testid}>
      <h2 className="m-0 flex items-center gap-2 text-[17px] font-bold">
        <Icon className="w-5 h-5 text-primary" />
        {title}
      </h2>
      <div className="flex flex-col gap-1.5 text-[15px]">{children}</div>
    </section>
  )
}

// Aldoo's standard return policy: the part of the Terms that sums it up (the Returns policy has it in full)
const STANDARD_RETURN_POLICY = "/terms-of-use#standard-return-policy"

/**
 * Where the seller's return terms are: its own page (a link), its text further
 * down this page, or Aldoo's standard return policy — the seller's own applying,
 * never less favourable to a buyer than Aldoo's standard one or the law.
 */
function ReturnPolicyLine({ seller }) {
  const t = useT()
  const mode = seller.returnPolicyMode || (seller.returnPolicyUrl ? "link" : "")
  if (mode === "aldoo") {
    return (
      <span className="flex flex-col gap-1" data-testid="seller-return-policy-aldoo">
        <span>{t`This seller follows Aldoo's standard return policy.`}</span>
        <a href={STANDARD_RETURN_POLICY} className="inline-flex items-center gap-2 text-primary underline" data-testid="seller-return-policy">
          <DocumentTextIcon className="w-4 h-4" />
          {t`Read Aldoo's standard return policy`}
        </a>
      </span>
    )
  }
  const href = mode === "link" ? seller.returnPolicyUrl : mode === "text" && seller.returnPolicyText ? "#return-policy" : ""
  if (!href) return null
  return (
    <>
      <a href={href} {...(mode === "link" ? { target: "_blank", rel: "noreferrer" } : {})} className="inline-flex items-center gap-2 text-primary underline" data-testid="seller-return-policy">
        <DocumentTextIcon className="w-4 h-4" />
        {t`The seller's return policy`}
      </a>
      <span className="text-[13px] text-text_secondary" data-testid="seller-return-policy-note">
        {t`The seller's own return terms apply, but they are never less favourable to you than Aldoo's standard return policy or the law.`}
      </span>
    </>
  )
}

// a long policy shows its start, and the rest on asking
const LONG_POLICY = 700

/** The seller's return policy as it wrote it: its lines kept, folded when long. */
function ReturnPolicyText({ text }) {
  const t = useT()
  const long = text.length > LONG_POLICY || text.split("\n").length > 14
  const [open, setOpen] = React.useState(false)
  return (
    <section id="return-policy" className="md:col-span-2 scroll-mt-24 rounded-2xl border border-bordered bg-paper p-5 flex flex-col gap-2" data-testid="seller-return-policy-text">
      <h2 className="m-0 flex items-center gap-2 text-[17px] font-bold">
        <DocumentTextIcon className="w-5 h-5 text-primary" />
        {t`The seller's return policy`}
      </h2>
      <div className={twMerge("relative whitespace-pre-line break-words text-[15px] leading-relaxed", long && !open && "max-h-[260px] overflow-hidden")}>
        {text}
        {long && !open && <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-paper to-transparent" />}
      </div>
      {long && (
        <button type="button" onClick={() => setOpen(!open)} className="self-start text-primary underline text-[15px]" data-testid="seller-return-policy-toggle">
          {open ? t`Show less` : t`Show more`}
        </button>
      )}
    </section>
  )
}

/** Who the seller is: how to reach them, how they deliver, how goods go back. */
function AboutTab({ seller }) {
  const t = useT()
  return (
    <div className="mx-auto grid w-full max-w-[960px] gap-5 md:grid-cols-2" data-testid="seller-about">
      <InfoCard icon={EnvelopeIcon} title={t`Contact`} testid="seller-contact">
        {seller.email && (
          <a href={`mailto:${seller.email}`} className="inline-flex items-center gap-2 text-primary underline">
            <EnvelopeIcon className="w-4 h-4" />
            {seller.email}
          </a>
        )}
        {seller.phone && (
          <a href={`tel:${seller.phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 text-primary underline">
            <PhoneIcon className="w-4 h-4" />
            {seller.phone}
          </a>
        )}
        {!seller.email && !seller.phone && <span className="text-text_secondary">{t`The seller has not shared contact details yet.`}</span>}
      </InfoCard>

      <InfoCard icon={TruckIcon} title={t`Delivery`} testid="seller-delivery">
        {seller.deliveryDays ? <span>{t("Usually delivered within %s working days.", seller.deliveryDays)}</span> : <span className="text-text_secondary">{t`Delivery times are shown on each product.`}</span>}
        {seller.freeShippingFrom && <span>{t("Free shipping on orders over %s.", formatMoney(seller.freeShippingFrom.amount, seller.freeShippingFrom.currency))}</span>}
        {/* the ways its parcels reach a buyer: each courier, its logo, its enabled ways under it */}
        {seller.deliveryMethods?.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-x-10 gap-y-4" data-testid="seller-delivery-methods">
            {Object.values(
              seller.deliveryMethods.reduce((groups, method) => {
                const key = method.providerId || method.provider
                ;(groups[key] = groups[key] || { key, name: method.providerName || method.provider, logo: method.logo, methods: [] }).methods.push(method)
                return groups
              }, {})
            ).map((courier) => (
              <div key={courier.key} className="flex flex-col gap-2" data-testid="seller-delivery-provider">
                {courier.logo ? (
                  <img src={cmsUrl(courier.logo)} alt={courier.name} className="h-9 w-auto max-w-[140px] object-contain object-left" />
                ) : (
                  <span className="text-[15px] font-semibold">{courier.name}</span>
                )}
                <ul className="m-0 pl-5 list-disc flex flex-col gap-1 text-[14px] marker:text-primary">
                  {courier.methods.map((method) => (
                    <li key={method.method}>{t(method.label)}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}
      </InfoCard>

      <InfoCard icon={ArrowUturnLeftIcon} title={t`Returns`} testid="seller-returns">
        <span>{t("You can return items within %s days of receiving them, without giving a reason.", seller.returnDays)}</span>
        <span className="text-text_secondary">
          {/* "from your order" opens the buyer's orders, where the return is asked for */}
          {t`Ask for a return`}{" "}
          <a href="/user/orders" className="text-primary underline" data-testid="seller-returns-orders">
            {t`from your order on the site`}
          </a>
          {t`: the seller answers with where to send the items.`}
        </span>
        <ReturnPolicyLine seller={seller} />
      </InfoCard>

      {/* the seller's own return terms, written in its profile: the page's #return-policy */}
      {seller.returnPolicyMode === "text" && seller.returnPolicyText && <ReturnPolicyText text={seller.returnPolicyText} />}

      {seller.returnAddress && (
        <InfoCard icon={MapPinIcon} title={t`Return address`} testid="seller-return-address">
          <span className="whitespace-pre-line">{seller.returnAddress}</span>
        </InfoCard>
      )}
    </div>
  )
}

/**
 * /seller/:slug (the products) and /seller/:slug/about (the seller) — the
 * logo, the name and the badge; tabs; the paged product grid, or the seller's
 * contacts and terms.
 */
export default function SellerPage() {
  const t = useT()
  const pageContext = usePageContext()
  const language = useSiteLanguage()
  const { slug, isProducts } = sellerSlugFromPath(pageContext.urlPathname)
  const ssr = pageContext.storefrontSeller
  const initial = ssr?.slug === slug ? ssr : null

  const { result: loaded, pending } = useAsync(() => LoadSeller({ slug, language }), [slug, language], { initial, enabled: !!slug })
  const seller = loaded && !loaded.error ? loaded : initial

  usePageTitle(seller?.name)

  if (!slug) return <div className="py-32 text-center text-text_secondary">{t`No information available for this seller.`}</div>
  if (!seller) return pending ? <Spinner className="py-32" /> : <div className="py-32 text-center text-text_secondary">{t`No information available for this seller.`}</div>

  return (
    <div className="flex w-full flex-col items-center" data-testid="seller-page">
      {/* Header: the logo, the name and the badge, centred */}
      <div className={twMerge("w-full border-b border-bordered py-4 flex flex-col items-center gap-3", PAGE_PX)}>
        {seller.logo && <img src={orgLogoUrl(seller.id, seller.logoUpdatedAt)} alt="" className="h-16 w-auto max-w-[220px] object-contain" data-testid="seller-logo" />}
        <div className="flex items-center gap-2">
          <Typography variant="h1" className="m-0 text-center">
            {seller.name}
          </Typography>
          {seller.verified && <CheckBadgeIcon className="w-7 h-7 text-primary" title={t`Verified seller`} aria-label={t`Verified seller`} />}
        </div>
      </div>

      {/* Tabs */}
      <div className={twMerge("w-full pt-3 mb-5 md:mb-[50px]", PAGE_PX)}>
        <div className="mx-auto flex max-w-[1360px] flex-row justify-center gap-4">
          <TabChip label={t`Products`} to={`/seller/${slug}`} active={isProducts} />
          <TabChip label={t`About the seller`} to={`/seller/${slug}/about`} active={!isProducts} />
        </div>
      </div>

      {isProducts ? (
        // as wide as a category page: the grid fits as many cards as the screen does
        <div className="w-[90%] max-w-w1_xl pb-10 md:pb-20">
          <ProductsTab slug={slug} />
        </div>
      ) : (
        <div className={twMerge("w-full pb-10 md:pb-20", PAGE_PX)}>
          <AboutTab seller={seller} />
        </div>
      )}
    </div>
  )
}

SellerPage.onBeforeRender = onBeforeRender
